import React from 'react'
import { EmptyState, SearchWithFilter } from '@enamelai/design-system'
import { Col, Container, Row } from 'react-bootstrap'
import { useOutletContext } from 'react-router-dom'
import PaymentDetailsLayout from '@components/pages/Billing/Payment/PaymentDetailsLayout'
import PaymentHistoryTable from '@components/pages/Billing/Payment/PaymentHistoryTable.component'
import PaymentOutstandingTable from '@components/pages/Billing/Payment/PaymentOutstandingTable.component'
import type { BillingOutletContext } from '@components/pages/Billing/Billing.component'
import usePayment from '@components/pages/Billing/Payment/usePayment'
import '../../components/pages/Billing/Payment/PaymentTableStyle.css'
import { useNotificationToast } from '@context/NotificationToastContext'
import { hasBillingListNarrowed } from '@utils/billing.utils'

const HISTORY_FILTERS = {
  method: ['Cash', 'Card', 'Online'],
}

const HISTORY_FILTER_LABELS = {
  method: 'Payment Method',
}

function BillingPaymentsPage(): React.ReactElement {
  const { hooks } = useOutletContext<BillingOutletContext>()
  const { showToast } = useNotificationToast()
  const paymentHooks = usePayment(showToast, {
    suppressTableLoading: hooks.suppressTableLoading,
  })

  const showOutstandingEmpty = paymentHooks.filteredPayments.length === 0
  const historyListNarrowed = hasBillingListNarrowed(
    paymentHooks.historySearchQuery,
    paymentHooks.historyFilters,
  )
  const showHistoryEmpty =
    !paymentHooks.loadingPaymentHistory &&
    paymentHooks.filteredHistory.length === 0
  const showUnifiedEmpty = showOutstandingEmpty && showHistoryEmpty

  const historySearch = (
    <SearchWithFilter
      placeholder="Search"
      value={paymentHooks.historySearchQuery}
      onChange={paymentHooks.handleHistorySearchChange}
      filters={HISTORY_FILTERS}
      filterLabels={HISTORY_FILTER_LABELS}
      selectedFilters={paymentHooks.historyFilters}
      onFilterChange={paymentHooks.handleHistoryFilterChange}
    />
  )

  if (showUnifiedEmpty) {
    return (
      <Container fluid className="p-0 billing-tab-panel">
        {historyListNarrowed ? (
          <div className="payment-history-header">
            <h3 className="payment-history-title">Payment History</h3>
            {historySearch}
          </div>
        ) : null}
        <EmptyState
          className="payment-unified-empty"
          iconClass="mgc mgc_bill_2_fill"
          title="No payments found"
          description={
            historyListNarrowed
              ? 'No payments match your search.'
              : "You don't have any payments on record yet."
          }
          actionLabel={historyListNarrowed ? undefined : 'Add Invoice'}
          onAction={historyListNarrowed ? undefined : hooks.handleAddInvoice}
        />
      </Container>
    )
  }

  return (
    <Container fluid className="p-0 billing-tab-panel">
      <Row className="gx-2">
        <Col style={{ minWidth: 0 }}>
          <div className="payment-outstanding-card">
            {showOutstandingEmpty ? (
              <EmptyState
                className="payment-outstanding-empty"
                iconClass="mgc mgc_bill_2_fill"
                title="No invoices selected"
                description="Add invoices to record a payment."
                actionLabel="Add Invoice"
                onAction={hooks.handleAddInvoice}
              />
            ) : (
              <PaymentOutstandingTable
                filteredPayments={paymentHooks.filteredPayments}
                handleRemoveFromSelection={
                  paymentHooks.handleRemoveFromSelection
                }
              />
            )}
          </div>

          <div className="payment-history-card">
            <div className="payment-history-header">
              <h3 className="payment-history-title">Payment History</h3>
              {historySearch}
            </div>
            {showHistoryEmpty ? (
              <EmptyState
                className="payment-history-empty"
                iconClass="mgc mgc_bill_2_fill"
                title="No payments found"
                description={
                  historyListNarrowed
                    ? 'No payments match your search.'
                    : "You don't have any payments on record yet."
                }
              />
            ) : (
              <PaymentHistoryTable
                filteredHistory={paymentHooks.filteredHistory}
                paymentHistoryCount={paymentHooks.paymentHistoryCount}
                currentPage={paymentHooks.currentPage}
                pageSize={paymentHooks.pageSize}
                handlePageChange={paymentHooks.handlePageChange}
                handlePageSizeChange={paymentHooks.handlePageSizeChange}
                handlePaymentHistoryTableSortChange={
                  paymentHooks.handlePaymentHistoryTableSortChange
                }
                allPatients={paymentHooks.paymentDetails.allPatients}
              />
            )}
          </div>
        </Col>
        <Col
          className="payment-details-col"
          style={{ minWidth: '25rem', maxWidth: '37.5rem' }}
        >
          <PaymentDetailsLayout
            selectedInvoices={paymentHooks.filteredPayments}
            paymentDetails={paymentHooks.paymentDetails}
          />
        </Col>
      </Row>
    </Container>
  )
}

export default BillingPaymentsPage
